import { useEffect, useState } from "react";
import { Button, ConfirmationDialog, RenderName, TableHeader, TableRow, TextInput } from "../../components";
import { pendingHeaders } from "./OfficeScreenData";
import { Office, OfficeState } from "../../models/office.model";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { searchPendingOffice, sortObj, updateOfficeState } from "../../redux/slices/OfficeSlice";
import { useSelector } from "react-redux";
import moment from "moment";
import { useNavigate } from "react-router-dom";

const Pending = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [showVerifyDialog, setShowVerifyDialog] = useState<Office | undefined>(undefined);
  const [showDenyReason, setShowDenyReason] = useState(false);
  const [denyReason, setDenyReason] = useState("");
  const [extraClasses] = useState<string[]>(pendingHeaders.map(head => head.extraClasses));
  const { pendingOffices, sort } = useSelector((state: RootState) => state.office);

  // const onPageChange = (e: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
  //   updateTable({ sortedBy: sort.sortedBy, pageNumber: newPage, rowsPerPage: sort.rowsPerPage }, searchTerm);
  // };

  // const onRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
  //   updateTable({ sortedBy: sort.sortedBy, pageNumber: 0, rowsPerPage: parseInt(event.target.value, 10) }, searchTerm);
  // };

  const updateTable = (sortObj?: sortObj, searchText?: string) => {
    const pageNumToBeCalled = sortObj?.pageNumber ?? sort.pageNumber;
    const sortBy = sortObj?.sortedBy ?? sort.sortedBy;
    const searchT = searchText ?? "";
    dispatch(
      searchPendingOffice({
        searchTerm: searchT,
        page: 0,
        size: 100,
        sortType: sortBy,
        officeStates: [OfficeState.Pending],
      })
    );
  };

  const cancel = () => {
    setShowVerifyDialog(undefined);
    setShowDenyReason(false);
    setDenyReason("");
    updateTable();
  };

  const handleReason = async () => {
    if (showVerifyDialog?.officeId) {
      await dispatch(
        updateOfficeState({
          officeId: showVerifyDialog?.officeId,
          state: showDenyReason ? OfficeState.Rejected : OfficeState.Draft,
          reason: denyReason,
        })
      );
      cancel();
    }
  };

  const renderContent = () => {
    return (
      <div className="pb-5 overflow-hidden">
        <h2 className="uppercase font-bold">{showDenyReason ? "Please provide reason for denial." : "Office information"}</h2>
        <div className="mt-3">
          {!showDenyReason ? (
            <ul className="flex flex-col gap-3">
              <li>
                <p className="font-medium text-sm">Office Name</p>
                <p className="font-medium text-xl">{showVerifyDialog?.name}</p>
              </li>
              <li>
                <p className="font-medium text-sm">Office Address</p>
                <p className="font-medium text-xl">
                  {showVerifyDialog?.street2 ? `${showVerifyDialog?.street2}, ` : ""}
                  {showVerifyDialog?.street}, {showVerifyDialog?.state}, {showVerifyDialog?.zip}
                </p>
              </li>
              <li className="grid grid-cols-2">
                <div>
                  <p className="font-medium text-sm">Office Phone Number</p>
                  <p className="font-medium text-xl">{showVerifyDialog?.phone}</p>
                </div>
                <div>
                  <p className="font-medium text-sm">Office Fax Number</p>
                  <p className="font-medium text-xl">{showVerifyDialog?.fax}</p>
                </div>
              </li>
              <li className="grid grid-cols-2">
                <div>
                  <p className="font-medium text-sm">Primary Contact </p>
                  <p className="font-medium text-xl">{showVerifyDialog?.contactName}</p>
                </div>
                <div>
                  <p className="font-medium text-sm">Primary Contact Email </p>
                  <p className="font-medium text-xl !truncate">{showVerifyDialog?.contactEmail}</p>
                </div>
              </li>
            </ul>
          ) : (
            <>
              <TextInput
                isTextArea
                extraInputClass="resize-none"
                placeholder="Write details here"
                maxLength={250}
                counterMessage="250"
                showCounter
                value={denyReason}
                onChangeFunc={e => setDenyReason(e.target.value)}
              />
              <p>The office will be notified of this information.</p>
            </>
          )}
        </div>
        <div className="absolute bottom-7 right-7 flex justify-end gap-5 mt-5">
          {showDenyReason ? (
            <>
              <Button text="Cancel" variant="Outlined" onClick={() => cancel()} />
              <Button
                text="Deny"
                disabled={!denyReason.length}
                onClick={() => {
                  handleReason();
                }}
              />
            </>
          ) : (
            <>
              <Button text="Deny" variant="Outlined" onClick={() => setShowDenyReason(true)} />
              <Button
                text="Verify"
                onClick={() => {
                  handleReason();
                }}
              />
            </>
          )}
        </div>
      </div>
    );
  };

  const handleVerifyClick = (e: React.MouseEvent<HTMLButtonElement>, item: Office) => {
    e.stopPropagation();
    setShowVerifyDialog(item);
  };

  useEffect(() => {
    updateTable();
  }, []);

  if (pendingOffices.length)
    return (
      <div>
        <div className="flex justify-between items-center border-b border-docsigna-blue-light">
          {pendingHeaders.map((head, index) => (
            <TableHeader
              title={head.title}
              index={index}
              key={index}
              sortIcon={null}
              handleSort={() => null}
              extraClassesHeader={head.extraClasses}
              isClickable={head.isClickable}
            />
          ))}
        </div>
        {pendingOffices.map((item, index) => {
          const rowData = {
            date: moment(item.createdDate).format("ll"),
            office: <RenderName title={item.name} subTitle="" />,
            addressLine1: (
              <RenderName title={[item.street, item.street1].filter(Boolean).join(", ")} subTitle={item.city + ", " + item.state + " " + item.zip} />
            ),
            contact: item.phone,
            verify: (
              <button
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleVerifyClick(e, item)}
                className="!pointer-events-auto text-sm text-center px-5 py-2.5 font-semibold leading-none rounded-full border shadow-none outline-none transition duration-500 text-white bg-docnote-orange hover:bg-white hover:text-docnote-orange border-docnote-orange"
              >
                Verify
              </button>
            ),
          };
          return (
            <TableRow
              data={Object.values(rowData)}
              key={index}
              id={""}
              tooltipVisible={false}
              handleOpenTooltip={() => null}
              handleCloseTooltip={() => null}
              tooltipOptions={[]}
              extraClassesRow={extraClasses}
              onRowClick={() => {
                navigate("/offices/" + item.officeId + "/requests");
              }}
            />
          );
        })}

        <ConfirmationDialog
          open={!!showVerifyDialog}
          description={renderContent()}
          showCrossIcon={!showDenyReason}
          title={""}
          canClickOutSide={false}
          successButtonText={""}
          handleSuccess={() => undefined}
          failureButtonText={""}
          handleFailure={() => setShowVerifyDialog(undefined)}
        />
      </div>
    );
  else return <p className="font-semibold">No offices pending approval</p>;
};

export default Pending;
